import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Calendar, Clock, Share2, Twitter, Linkedin, Copy } from "lucide-react";
import { useState } from "react";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Seo, { SITE } from "@/components/Seo";
import { getPostBySlug, getRelatedPosts, CATEGORIES } from "@/data/blogPosts";

function Block({ block }) {
    switch (block.type) {
        case "lead":
            return <p className="text-[17px] sm:text-lg md:text-xl text-ink leading-[1.6] font-medium mb-7 sm:mb-8">{block.value}</p>;
        case "h2":
            return <h2 className="font-heading font-bold text-xl sm:text-2xl md:text-3xl text-ink mt-10 sm:mt-12 mb-4 sm:mb-5 tracking-tight">{block.value}</h2>;
        case "h3":
            return <h3 className="font-heading font-bold text-lg sm:text-xl text-ink mt-8 sm:mt-10 mb-3 sm:mb-4 tracking-tight">{block.value}</h3>;
        case "p":
            return <p className="text-[15px] sm:text-base text-muted-ink leading-[1.8] sm:leading-[1.85] mb-4 sm:mb-5">{block.value}</p>;
        case "ul":
            return (
                <ul className="space-y-3 mb-6 pl-1">
                    {block.value.map((item, i) => (
                        <li key={i} className="flex gap-3 text-[15px] sm:text-base text-muted-ink leading-[1.7]">
                            <span className="mt-2 sm:mt-2.5 shrink-0 w-1.5 h-1.5 rounded-full bg-brand-violet" />
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
            );
        case "callout":
            return (
                <div className="my-7 sm:my-8 p-5 md:p-6 rounded-2xl border border-line bg-card-muted relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-brand-violet to-brand-cyan" />
                    <p className="text-[15px] sm:text-base md:text-lg text-ink font-medium leading-[1.6] pl-3">{block.value}</p>
                </div>
            );
        case "code":
            return (
                <pre className="my-6 p-4 rounded-xl bg-card-muted border border-line overflow-x-auto">
                    <code className="font-mono text-[11.5px] sm:text-[12.5px] text-ink leading-relaxed whitespace-pre">{block.value}</code>
                </pre>
            );
        default:
            return null;
    }
}

function ShareBar({ post }) {
    const [copied, setCopied] = useState(false);
    const url = `${SITE.url}/blog/${post.slug}`;
    const text = encodeURIComponent(`${post.title} — DeCloudX`);
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (_) {}
    };
    return (
        <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-faint-ink mr-1">Share</span>
            <a
                href={`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(url)}`}
                target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-full surface flex items-center justify-center hover:border-line-strong hover:bg-card-muted transition-all text-muted-ink hover:text-ink"
                aria-label="Share on X"
                data-testid="share-twitter"
            >
                <Twitter size={14} strokeWidth={1.8} />
            </a>
            <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
                target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 rounded-full surface flex items-center justify-center hover:border-line-strong hover:bg-card-muted transition-all text-muted-ink hover:text-ink"
                aria-label="Share on LinkedIn"
                data-testid="share-linkedin"
            >
                <Linkedin size={14} strokeWidth={1.8} />
            </a>
            <button
                onClick={handleCopy}
                className="w-9 h-9 rounded-full surface flex items-center justify-center hover:border-line-strong hover:bg-card-muted transition-all text-muted-ink hover:text-ink"
                aria-label="Copy link"
                data-testid="share-copy"
            >
                <Copy size={14} strokeWidth={1.8} />
            </button>
            {copied && (
                <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-cyan">Copied</span>
            )}
        </div>
    );
}

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPostBySlug(slug);

    if (!post) return <Navigate to="/blog" replace />;

    const cat = CATEGORIES[post.category];
    const related = getRelatedPosts(slug, 3);
    const url = `${SITE.url}/blog/${post.slug}`;

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "mainEntityOfPage": { "@type": "WebPage", "@id": url },
        "headline": post.title,
        "description": post.excerpt,
        "image": `${SITE.url}/og-image.png`,
        "author": { "@type": "Organization", "name": post.author, "url": SITE.url },
        "publisher": {
            "@type": "Organization",
            "name": "DeCloudX",
            "logo": { "@type": "ImageObject", "url": `${SITE.url}/decloudx-logo.png` },
        },
        "datePublished": post.publishedAt,
        "dateModified": post.publishedAt,
        "keywords": post.tags.join(", "),
        "articleSection": post.category,
    };

    const breadcrumbsLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE.url}/blog` },
            { "@type": "ListItem", position: 3, name: post.title, item: url },
        ],
    };

    return (
        <div className="min-h-screen bg-page">
            <Seo
                title={post.title}
                description={post.excerpt}
                keywords={post.keywords + ", " + post.tags.join(", ")}
                path={`/blog/${post.slug}`}
                type="article"
                article={{
                    publishedAt: post.publishedAt,
                    updatedAt: post.publishedAt,
                    author: post.author,
                    category: post.category,
                    tags: post.tags,
                }}
                jsonLd={jsonLd}
            />
            {/* Second JSON-LD for breadcrumbs */}
            <Seo
                title={post.title}
                description={post.excerpt}
                path={`/blog/${post.slug}`}
                noindex={false}
                jsonLd={breadcrumbsLd}
            />

            <Nav />

            {/* Hero / cover */}
            <section className="relative pt-24 sm:pt-28 md:pt-32 pb-8 sm:pb-10 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-25"
                    style={{ background: cat.gradient }}
                />
                <div className="absolute inset-0 hero-glow-bg pointer-events-none" />
                <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />

                <div className="relative max-w-3xl mx-auto px-5 sm:px-6 md:px-10">
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-ink hover:text-ink mb-8 transition-colors"
                        data-testid="back-to-blog"
                    >
                        <ArrowLeft size={12} strokeWidth={1.8} /> All articles
                    </Link>

                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-5 sm:mb-6"
                    >
                        <span
                            className="inline-flex items-center px-2.5 py-1 rounded-md font-mono text-[10px] uppercase tracking-[0.18em] text-white"
                            style={{ background: cat.gradient }}
                        >
                            {post.category}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-faint-ink inline-flex items-center gap-1.5">
                            <Calendar size={11} strokeWidth={1.8} />
                            {new Date(post.publishedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                        </span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-faint-ink inline-flex items-center gap-1.5">
                            <Clock size={11} strokeWidth={1.8} />
                            {post.readMins} min read
                        </span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.05 }}
                        className="font-heading font-black text-[1.75rem] sm:text-[2.25rem] md:text-[2.6rem] lg:text-[3rem] leading-[1.08] tracking-[-0.025em] text-ink"
                        data-testid="post-title"
                    >
                        {post.title}
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="mt-5 sm:mt-6 text-[15px] sm:text-base md:text-lg text-muted-ink leading-relaxed"
                    >
                        {post.excerpt}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.25 }}
                        className="mt-6 sm:mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-b border-line py-5"
                    >
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-mono text-[11px] font-bold" style={{ background: cat.gradient }}>
                                {post.author.split(" ").map(w => w[0]).slice(0,2).join("")}
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-ink">{post.author}</div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-faint-ink">DeCloudX</div>
                            </div>
                        </div>
                        <ShareBar post={post} />
                    </motion.div>
                </div>
            </section>

            {/* Article body */}
            <article className="relative pb-16 sm:pb-20">
                <div className="max-w-3xl mx-auto px-5 sm:px-6 md:px-10">
                    {post.content.map((b, i) => <Block key={i} block={b} />)}

                    {/* Tags */}
                    <div className="mt-12 pt-8 border-t border-line">
                        <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-faint-ink mb-3">Topics</div>
                        <div className="flex flex-wrap gap-2">
                            {post.tags.map((t) => (
                                <span key={t} className="chip">{t}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </article>

            {/* Related posts */}
            {related.length > 0 && (
                <section className="pb-20 md:pb-32 border-t border-line bg-card-muted/40 pt-12 sm:pt-16">
                    <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
                        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-faint-ink mb-2">More from DeCloudX</div>
                                <h2 className="font-heading font-bold text-2xl md:text-3xl text-ink tracking-tight">Related articles</h2>
                            </div>
                            <Link to="/blog" className="hidden md:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-ink hover:text-ink transition-colors">
                                All articles <ArrowUpRight size={12} strokeWidth={1.8} />
                            </Link>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 md:gap-7">
                            {related.map((p) => {
                                const rcat = CATEGORIES[p.category];
                                return (
                                    <Link
                                        key={p.slug}
                                        to={`/blog/${p.slug}`}
                                        className="group block surface-strong rounded-2xl overflow-hidden hover:shadow-md transition-all hover:-translate-y-0.5"
                                        data-testid={`related-${p.slug}`}
                                    >
                                        <div className="relative h-36 overflow-hidden" style={{ background: rcat.gradient }}>
                                            <div className="absolute inset-0 dot-grid opacity-30" />
                                            <div className="absolute inset-0 flex items-end p-4">
                                                <span className="inline-flex px-2 py-0.5 rounded-md font-mono text-[9px] uppercase tracking-[0.18em] text-white bg-white/15 backdrop-blur-sm border border-white/20">
                                                    {p.category}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-5">
                                            <h3 className="font-heading font-bold text-base text-ink leading-snug tracking-tight group-hover:text-brand-violet transition-colors">
                                                {p.title}
                                            </h3>
                                            <p className="mt-2.5 text-[13px] text-muted-ink leading-relaxed line-clamp-2">{p.excerpt}</p>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            <Footer />
        </div>
    );
}
